/**
 * The browser rollout worker: its wire format, its protocol, and its entry.
 *
 * Node has ./rollout-worker, which talks over `worker_threads` and posts
 * `Transition[][]` straight down the port. A browser `Worker` cannot use either
 * — the module is loaded by URL and the channel is `postMessage` — so this is
 * the second transport for the SAME collector. It is deliberately not a second
 * rollout: every stepping decision is `RolloutCollector`'s, exactly as in the
 * Node worker and in `createSyncBackend`, so the three paths cannot drift.
 *
 * ===========================================================================
 * WHY THE TRANSITIONS ARE PACKED
 * ===========================================================================
 *
 * An iteration is a few thousand `Transition`s, each holding a 70-element
 * `Float32Array`. Posting that array of objects makes the structured-clone
 * algorithm walk a few thousand objects and allocate a few thousand
 * ArrayBuffers on the receiving side, per worker, per iteration — on the main
 * thread, which is the one thread that must not be spending milliseconds on
 * bookkeeping. Packed into seven flat typed arrays the same batch is seven
 * TRANSFERS: no copy at all, and no per-transition allocation.
 *
 * `pack` and `unpack` are pure and exact inverses, which is the property worth
 * testing — a packing bug does not crash, it silently reorders experience, and
 * PPO trained on shuffled `(obs, action, logProb)` triples converges to
 * something plausible-looking and wrong.
 *
 * ===========================================================================
 * WHY THIS FILE IS SAFE TO IMPORT ANYWHERE
 * ===========================================================================
 *
 * The message handler is installed only when the module really is running as a
 * dedicated worker. The backend on the main thread imports this file for the
 * protocol types and for `unpackTrajectories`, and a Node test imports it to
 * check the round trip; neither may end up with an `onmessage` handler
 * attached to the window.
 */

import { OBS_SIZE, type Transition } from './types';
import {
  RolloutCollector,
  emptyEpisodeStats,
  type EpisodeStats,
  type TrainerConfig,
} from './trainer';

// ---------------------------------------------------------------------------
// Wire format
// ---------------------------------------------------------------------------

/**
 * One collection's transitions, flattened.
 *
 * `lengths` is what preserves the trajectory boundaries, and those boundaries
 * are load-bearing: the trainer runs GAE per trajectory, and a boundary lost in
 * transit leaks advantage backwards across an episode join.
 */
export interface PackedTrajectories {
  /** `count * OBS_SIZE`, trajectory by trajectory. */
  obs: Float32Array;
  actions: Int32Array;
  logProbs: Float64Array;
  rewards: Float64Array;
  values: Float64Array;
  /** 1 on the last step of an episode. `Transition.done` is a boolean, and a
   *  boolean has no typed array. */
  dones: Uint8Array;
  /** `Transition.bootstrap`, 0 where absent. Carried because a trajectory cut
   *  by a time limit needs it and losing it would teach the critic that every
   *  truncation is worth zero — see `Transition.done`. */
  bootstraps: Float64Array;
  /** Transitions in each trajectory, in order. Sums to `count`. */
  lengths: Int32Array;
}

export const packTrajectories = (
  trajectories: readonly (readonly Transition[])[]
): PackedTrajectories => {
  let count = 0;
  for (const trajectory of trajectories) count += trajectory.length;

  const packed: PackedTrajectories = {
    obs: new Float32Array(count * OBS_SIZE),
    actions: new Int32Array(count),
    logProbs: new Float64Array(count),
    rewards: new Float64Array(count),
    values: new Float64Array(count),
    dones: new Uint8Array(count),
    bootstraps: new Float64Array(count),
    lengths: Int32Array.from(trajectories.map(t => t.length)),
  };

  let at = 0;
  for (const trajectory of trajectories) {
    for (const step of trajectory) {
      // `set` rather than a loop: the observation is already the right type and
      // the right length, and this is the copy that dominates the pack.
      packed.obs.set(step.obs, at * OBS_SIZE);
      packed.actions[at] = step.action;
      packed.logProbs[at] = step.logProb;
      packed.rewards[at] = step.reward;
      packed.values[at] = step.value;
      packed.dones[at] = step.done ? 1 : 0;
      packed.bootstraps[at] = step.bootstrap ?? 0;
      at++;
    }
  }
  return packed;
};

export const unpackTrajectories = (packed: PackedTrajectories): Transition[][] => {
  const out: Transition[][] = [];
  let at = 0;
  for (let t = 0; t < packed.lengths.length; t++) {
    const length = packed.lengths[t];
    const trajectory: Transition[] = [];
    for (let i = 0; i < length; i++) {
      const transition: Transition = {
        // A view would alias the one big buffer and keep all of it alive for as
        // long as any transition survives; the batch builder copies out of
        // these anyway, so the slice is paid once here rather than held forever.
        obs: packed.obs.slice(at * OBS_SIZE, (at + 1) * OBS_SIZE),
        action: packed.actions[at],
        logProb: packed.logProbs[at],
        reward: packed.rewards[at],
        value: packed.values[at],
        done: packed.dones[at] === 1,
      };
      // Only ever set where the collector set it: an absent bootstrap and a
      // bootstrap of exactly zero mean the same thing to `buildTrainingBatch`,
      // but `undefined` is what the type says "not applicable" with.
      if (packed.bootstraps[at] !== 0) transition.bootstrap = packed.bootstraps[at];
      trajectory.push(transition);
      at++;
    }
    out.push(trajectory);
  }
  return out;
};

/** Every buffer in a packed batch, for `postMessage`'s transfer list. */
export const packedTransferables = (packed: PackedTrajectories): Transferable[] => [
  packed.obs.buffer,
  packed.actions.buffer,
  packed.logProbs.buffer,
  packed.rewards.buffer,
  packed.values.buffer,
  packed.dones.buffer,
  packed.bootstraps.buffer,
  packed.lengths.buffer,
];

// ---------------------------------------------------------------------------
// Protocol
// ---------------------------------------------------------------------------

export interface BrowserWorkerInit {
  config: TrainerConfig;
  /** Chunk index. Fixed for the worker's lifetime, because a chunk's seeds —
   *  and therefore its trajectories — are a function of it. */
  index: number;
}

export type BrowserWorkerRequest =
  | { kind: 'init'; init: BrowserWorkerInit }
  | {
      kind: 'collect';
      /** Echoed back, so a late reply from a previous iteration is ignorable. */
      id: number;
      params: Float64Array;
      steps: number;
      seed: number;
      opponent: Float64Array | null;
      /** Curriculum position; see `RolloutBackend.collect`. */
      difficulty?: number;
    };

export type BrowserWorkerResponse =
  | { kind: 'ready'; index: number }
  | { kind: 'batch'; id: number; packed: PackedTrajectories; stats: EpisodeStats }
  | { kind: 'error'; id: number; message: string };

// ---------------------------------------------------------------------------
// Entry
// ---------------------------------------------------------------------------

/**
 * True only inside a dedicated worker.
 *
 * Feature-detected off `globalThis` rather than with `self instanceof
 * DedicatedWorkerGlobalScope`, because that constructor is declared in
 * lib.webworker, this project compiles against lib.dom, and the two cannot both
 * be in scope — a worker and a window disagree about what `self` even is. The
 * pair of facts below is true in a worker and in nothing else: a worker global
 * scope exists, and there is no document.
 */
const inDedicatedWorker = (): boolean => {
  const scope = globalThis as { WorkerGlobalScope?: unknown; document?: unknown };
  return scope.WorkerGlobalScope !== undefined && scope.document === undefined;
};

/**
 * Install the handler. Exported so a test can drive the worker's logic through
 * a pair of fake ports without a browser, and called at module scope only when
 * this really is a worker.
 */
export const installWorker = (scope: {
  postMessage: (message: BrowserWorkerResponse, transfer?: Transferable[]) => void;
  addEventListener: (type: 'message', handler: (event: { data: unknown }) => void) => void;
}): void => {
  // Constructing a collector builds the soccar mesh and its BVH, which is why
  // there is one long-lived worker per chunk rather than one per request.
  let collector: RolloutCollector | null = null;

  scope.addEventListener('message', event => {
    const request = event.data as BrowserWorkerRequest;
    if (request.kind === 'init') {
      collector = new RolloutCollector(request.init.config, request.init.index);
      scope.postMessage({ kind: 'ready', index: request.init.index });
      return;
    }

    try {
      if (collector === null) throw new Error('collect before init');
      collector.setOpponent(request.opponent);
      const result = collector.collect(
        request.params,
        request.steps,
        request.seed,
        request.difficulty
      );
      const packed = packTrajectories(result.trajectories);
      scope.postMessage(
        { kind: 'batch', id: request.id, packed, stats: result.stats },
        packedTransferables(packed)
      );
    } catch (error) {
      // Reported rather than thrown: an uncaught throw in a worker surfaces as
      // an `error` event with no request id, and the backend would wait forever
      // for the reply that is never coming.
      scope.postMessage({
        kind: 'error',
        id: request.kind === 'collect' ? request.id : -1,
        message: error instanceof Error ? (error.stack ?? error.message) : String(error),
      });
    }
  });
};

/** An empty stats record, re-exported so the backend can merge onto one
 *  without importing the trainer for a single constructor. */
export const emptyStats = (): EpisodeStats => emptyEpisodeStats();

if (inDedicatedWorker()) {
  installWorker(
    self as unknown as Parameters<typeof installWorker>[0]
  );
}
